import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Empty, Field, Input, Modal, Notice, Panel, Select, Table, type ColumnDef } from '../../design/index.js'
import { useApi } from '../../app/api.js'
import { adminApi, type DelegateRequest, type RegisterRow } from '../../app/admin-api.js'
import { ErrorBlock, Loading, messageFor } from '../../framework/feedback.js'
import { formatDate, formatDateTime } from '../../framework/format.js'
import './admin.css'

/** What a sign-in attempt came to. Failures are the point of the register. */
const OUTCOMES: Readonly<Record<string, string>> = {
  SUCCESS: 'Signed in',
  BAD_PASSWORD: 'Wrong password',
  UNKNOWN_EMAIL: 'No such account',
  LOCKED: 'Account locked',
  SUSPENDED: 'Account suspended',
  IP_REFUSED: 'Network not allowed',
  DELEGATE_EXPIRED: 'Support access expired',
}

const isoDay = (d: Date): string => d.toISOString().slice(0, 10)

/** The period can be at most a year, as the reference says. */
export const periodProblem = (from: string, to: string): string | null => {
  if (!from || !to) return null
  const a = new Date(`${from}T00:00:00Z`).getTime()
  const b = new Date(`${to}T00:00:00Z`).getTime()
  if (b < a) return 'The To date is before the From date.'
  if (b - a > 366 * 86_400_000) return "The period (From - To Date) can't be more than 1 year."
  return null
}

interface RegisterFilters { show: string; from: string; to: string; userId: string }

/**
 * The User Login Register (USR-010): Show, From Date, To Date, User Name and
 * Apply; Login Date, User Name, Role, Login Email, IP Address, Browser, OS,
 * with a search box under each heading, the total, and Export.
 *
 * As in the reference (SET1-015) nothing is read until Apply: the page opens
 * on "Apply filters!", Show is required, and the dates and user appear once a
 * Show is chosen.
 */
export const LoginRegisterPage = () => {
  const api = useApi()
  const admin = adminApi(api)
  const today = new Date()
  const [draft, setDraft] = useState<RegisterFilters>({ show: '', from: '', to: '', userId: '' })
  const [applied, setApplied] = useState<RegisterFilters | null>(null)
  const [search, setSearch] = useState<Record<string, string>>({})
  const problem = periodProblem(draft.from, draft.to)

  const users = useQuery({ queryKey: ['adminUsers', api.companyId], queryFn: () => api.adminUsers() })
  const rows = useQuery({
    queryKey: ['loginRegister', api.companyId, applied],
    queryFn: () => admin.register(applied!),
    enabled: applied !== null,
  })

  const text = (r: RegisterRow, key: string): string => {
    switch (key) {
      case 'at': return formatDateTime(r.at)
      case 'outcome': return OUTCOMES[r.outcome] ?? r.outcome
      default: return String((r as unknown as Record<string, unknown>)[key] ?? '')
    }
  }
  const shown = useMemo(() => (rows.data ?? []).filter((r) => Object.entries(search).every(
    ([k, v]) => v.trim() === '' || text(r, k).toLowerCase().includes(v.trim().toLowerCase()),
  )), [rows.data, search])

  const COLS: ReadonlyArray<{ key: string; header: string; width?: number }> = [
    { key: 'at', header: 'Login Date', width: 190 },
    { key: 'userName', header: 'User Name', width: 170 },
    { key: 'roleName', header: 'Role', width: 150 },
    { key: 'email', header: 'Login Email' },
    { key: 'ip', header: 'IP Address', width: 140 },
    { key: 'browser', header: 'Browser', width: 120 },
    { key: 'os', header: 'OS', width: 120 },
    { key: 'outcome', header: 'What happened', width: 170 },
  ]
  const columns: ReadonlyArray<ColumnDef<RegisterRow>> = COLS.map((c) => ({
    key: c.key, header: c.header, ...(c.width ? { width: c.width } : {}),
    render: (r: RegisterRow) => (c.key === 'outcome' && r.outcome !== 'SUCCESS'
      ? <span style={{ color: 'var(--bad)', fontWeight: 600 }}>{text(r, c.key)}</span>
      : <span className={c.key === 'at' || c.key === 'ip' ? 'num' : undefined}>{text(r, c.key) || '—'}</span>),
  }))

  return (
    <>
      <Panel title="Filters">
        <div className="filter-bar" style={{ margin: 0 }}>
          <Field label="Show" required>
            <Select value={draft.show} placeholder="Choose"
              // The period starts as the last thirty days the first time a Show is picked.
              onChange={(show) => setDraft(draft.from || draft.to ? { ...draft, show }
                : { ...draft, show, from: isoDay(new Date(today.getTime() - 30 * 86_400_000)), to: isoDay(today) })}
              options={[{ value: 'ALL', label: 'All' }, { value: 'CURRENT', label: 'Currently Logged In' }]} />
          </Field>
          {draft.show ? (
            <>
              <Field label="From Date"><Input type="date" value={draft.from} onChange={(from) => setDraft({ ...draft, from })} /></Field>
              <Field label="To Date"><Input type="date" value={draft.to} onChange={(to) => setDraft({ ...draft, to })} /></Field>
              <Field label="User Name">
                <Select value={draft.userId} placeholder="Everyone" onChange={(userId) => setDraft({ ...draft, userId })}
                  options={(users.data ?? []).map((u) => ({ value: u.id, label: u.name }))} />
              </Field>
            </>
          ) : null}
          <div style={{ paddingBottom: 18 }}>
            <Button kind="primary" disabled={problem !== null || !draft.show} onClick={() => setApplied(draft)}>Apply</Button>
          </div>
        </div>
        {problem ? <Notice kind="bad">{problem}</Notice> : null}
      </Panel>

      <Panel
        title="User Login Register"
        aside={
          <div className="row">
            <span className="muted">Total Entries : {shown.length}</span>
            <Button kind="ghost" title="Export the current view to an Excel file" disabled={shown.length === 0}
              onClick={() => void admin.exportRows('User Login Register', COLS.map((c) => c.header), shown.map((r) => COLS.map((c) => text(r, c.key) || null)))}>
              Export
            </Button>
          </div>
        }
      >
        <div className="filter-bar" aria-label="Search the columns">
          {COLS.map((c) => (
            <Input key={c.key} aria-label={`Search ${c.header}`} placeholder={c.header}
              value={search[c.key] ?? ''} onChange={(v) => setSearch({ ...search, [c.key]: v })} />
          ))}
        </div>
        {applied === null ? (
          <Empty title="Apply filters!" body={["Fill the filter values and click 'Apply' button"]} />
        ) : rows.isLoading ? <Loading /> : rows.error ? <ErrorBlock error={rows.error} /> : (
          <Table columns={columns} rows={shown} getKey={(r) => r.id}
            empty={<Empty title="Nothing recorded" body={['No sign-in matches these filters.']} />} />
        )}
      </Panel>
    </>
  )
}

/* ---- delegate user requests (USR-011) ---------------------------------------- */

const STATUS_WORDS: Readonly<Record<DelegateRequest['status'], string>> = {
  PENDING: 'Pending', APPROVED: 'Approved', REJECTED: 'Rejected', REVOKED: 'Revoked',
}

/**
 * Delegate User Requests: somebody asks for an outside person — vendor
 * support, an auditor — to be given temporary access; an administrator
 * approves it, which creates the account, and revokes it when the work is done.
 */
export const DelegateRequestsPage = () => {
  const api = useApi()
  const admin = adminApi(api)
  const client = useQueryClient()
  const [asking, setAsking] = useState(false)
  const [form, setForm] = useState({ name: '', email: '', reason: '', days: '7' })
  const [error, setError] = useState<string | null>(null)
  const [issued, setIssued] = useState<{ name: string; password: string } | null>(null)
  const requests = useQuery({ queryKey: ['delegateRequests', api.companyId], queryFn: () => admin.delegateRequests() })

  const ask = useMutation({
    mutationFn: () => admin.requestDelegate({ name: form.name, email: form.email, reason: form.reason || null, days: Number(form.days) || 7 }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['delegateRequests', api.companyId] })
      setAsking(false)
      setForm({ name: '', email: '', reason: '', days: '7' })
      setError(null)
    },
    onError: (err) => setError(messageFor(err)),
  })
  const decide = useMutation({
    mutationFn: ({ row, decision }: { row: DelegateRequest; decision: 'APPROVE' | 'REJECT' | 'REVOKE' }) =>
      admin.decideDelegate(row.id, decision).then((r) => ({ ...r, row })),
    onSuccess: (r) => {
      client.setQueryData(['delegateRequests', api.companyId], r.requests)
      if (r.temporaryPassword) setIssued({ name: r.row.requestedName, password: r.temporaryPassword })
      setError(null)
    },
    onError: (err) => setError(messageFor(err)),
  })

  if (requests.isLoading) return <Loading />
  if (requests.error) return <ErrorBlock error={requests.error} />

  const columns: ReadonlyArray<ColumnDef<DelegateRequest>> = [
    { key: 'requestedAt', header: 'Date', width: 130, render: (r) => formatDate(r.requestedAt.slice(0, 10)) },
    { key: 'requestedName', header: 'Requested Name', render: (r) => <><div>{r.requestedName}</div>{r.reason ? <div className="cell-sub">{r.reason}</div> : null}</> },
    { key: 'requestedEmail', header: 'Requested Email', width: 240 },
    { key: 'days', header: 'Days', width: 80, align: 'right', render: (r) => <span className="num">{r.days}</span> },
    { key: 'status', header: 'Status', width: 120, render: (r) => STATUS_WORDS[r.status] },
    { key: 'action', header: 'Action', width: 220,
      render: (r) => (
        r.status === 'PENDING' ? (
          <span className="row">
            <Button kind="primary" disabled={decide.isPending} onClick={() => decide.mutate({ row: r, decision: 'APPROVE' })}>Approve</Button>
            <Button kind="ghost" disabled={decide.isPending} onClick={() => decide.mutate({ row: r, decision: 'REJECT' })}>Reject</Button>
          </span>
        ) : r.status === 'APPROVED' ? (
          <Button kind="ghost" disabled={decide.isPending} onClick={() => decide.mutate({ row: r, decision: 'REVOKE' })}>Revoke Access</Button>
        ) : <span className="muted">{r.decidedByName ?? ''}</span>
      ) },
  ]

  return (
    <>
      <div className="row" style={{ margin: '20px 0 12px' }}>
        <Button kind="primary" onClick={() => setAsking(true)}>Request delegate access</Button>
      </div>
      {error ? <div style={{ marginBottom: 12 }}><Notice kind="bad">{error}</Notice></div> : null}
      <Table columns={columns} rows={requests.data ?? []} getKey={(r) => r.id}
        empty={<Empty title="No requests" body={['Requests for temporary support access appear here.']} />} />

      {asking ? (
        <Modal title="Request delegate access" onClose={() => setAsking(false)}
          actions={<><Button kind="ghost" onClick={() => setAsking(false)}>Cancel</Button>
            <Button kind="primary" disabled={ask.isPending || form.name.trim().length < 2 || !form.email.includes('@')} onClick={() => ask.mutate()}>Request</Button></>}>
          <Field label="Name" required><Input value={form.name} onChange={(name) => setForm({ ...form, name })} /></Field>
          <Field label="Email" required><Input value={form.email} onChange={(email) => setForm({ ...form, email })} /></Field>
          <Field label="Why"><Input value={form.reason} onChange={(reason) => setForm({ ...form, reason })} /></Field>
          <Field label="For how many days?" hint="At most ninety."><Input value={form.days} onChange={(days) => setForm({ ...form, days: days.replace(/\D/g, '') })} /></Field>
        </Modal>
      ) : null}
      {issued ? (
        <Modal title="Their temporary password" tone="warn" onClose={() => setIssued(null)}
          actions={<Button kind="primary" onClick={() => setIssued(null)}>I have taken a note of it</Button>}>
          <p>Give this to <strong>{issued.name}</strong>. It is shown once, and the access ends on its own.</p>
          <p className="temp-password num">{issued.password}</p>
        </Modal>
      ) : null}
    </>
  )
}
